import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { classes } from '../styles';
import InfoBox from '../../../Components/CustomUIElements/InfoBox';
import FormSelectField from '../../../Components/CustomUIElements/FormSelectField';
import MultiSelect, { IOptionType } from '../../../Components/CustomUIElements/MultiSelect';
import NoAutoStatusSVG from '../AutoStatuses/NoAutoStatusSVG';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';

// Helper functions
const createRequestOptions = (method, body = null) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body && { body: JSON.stringify(body) })
});
const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));

export default function AutoTagging({
  jobId,
  setSnackbar,
  handleEditTabChange
}: {
  jobId: number;
  setSnackbar: any;
  handleEditTabChange?: (e: any, tabIndex: number) => void;
}) {
  const [hoveredField, setHoveredField] = useState([-1, -1]);
  const [autoTagConditions, setAutoTagConditions] = useState([]);

  // Transform condition data to include fields array
  const transformCondition = useCallback(
    (condition) => ({
      ...condition,
      tags: condition.tags || [],
      fields: [
        {
          id: condition.id,
          question: { id: condition.field_id, title: condition.field?.title || '' },
          choice: { id: condition.field_choice_id, name: condition.field_choice?.name || '' }
        }
      ]
    }),
    []
  );

  const { data: radioQuestions, isLoading: radioQuestionsIsLoading } = useQuery({
    queryKey: ['radioQuestions', jobId],
    queryFn: async () => {
      const response = await fetch(`/api/v4/fields?job_id=${jobId}`, { method: 'GET' }).then(
        (response) => response.json()
      );
      return response.fields.filter((question) => question.type === 'RadioButtonField');
    }
  });

  const { data: tagsData } = useQuery({
    queryKey: ['tags', jobId],
    queryFn: async () => {
      const response = await fetch(`/api/v4/tags?simple=true`, { method: 'GET' }).then((response) =>
        response.json()
      );
      return response;
    }
  });

  // Extract the tags array from the response
  const tags = tagsData?.tags || [];
  const fetchAutoTagConditions = useCallback(async () => {
    try {
      const response = await fetch(`/api/v4/jobs/${jobId}/auto_tag_conditions`, {
        method: 'GET'
      }).then((response) => response.json());
      setAutoTagConditions((response || []).map(transformCondition));
    } catch (error) {
      console.log(error);
      setSnackbar({ message: 'Error fetching auto tag conditions', state: 'error' });
    }
  }, [jobId, setSnackbar, transformCondition]);

  useEffect(() => {
    fetchAutoTagConditions();
  }, [fetchAutoTagConditions]);
  // Generic error handler for mutations
  const handleMutationError = (error: Error, defaultMessage: string) =>
    setSnackbar({ message: error.message || defaultMessage, state: 'error' });

  // Create mutation for adding a new auto tag condition
  const addAutoTagConditionMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions`,
        createRequestOptions('POST', {
          auto_tag_condition: {
            field_id: radioQuestions[0]?.id,
            field_choice_id: radioQuestions[0]?.field_choices[0]?.id,
            tag_ids: [tags[0]?.id]
          }
        })
      );
      if (!response.ok) throw new Error('Error adding auto tag condition');
      return response.json();
    },
    onSuccess: (data) => {
      setAutoTagConditions([...autoTagConditions, transformCondition(data)]);
    },
    onError: (error: Error) => handleMutationError(error, 'Error creating auto tag condition')
  });

  // Create mutation for deleting an auto tag condition
  const deleteAutoTagConditionMutation = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions/${id}`,
        createRequestOptions('DELETE')
      );
      if (!response.ok) throw new Error('Error deleting auto tag condition');
    },
    onMutate: async ({ index }) => {
      const autoTagConditionsCopy = deepCopy(autoTagConditions);
      autoTagConditionsCopy.splice(index, 1);
      setAutoTagConditions(autoTagConditionsCopy);
      return { previousAutoTagConditions: autoTagConditions };
    },
    onError: (error: Error, _, context) => {
      if (context?.previousAutoTagConditions) {
        setAutoTagConditions(context.previousAutoTagConditions);
      }
      handleMutationError(error, 'Error deleting auto tag condition');
    }
  });

  // Create mutation for updating an auto tag condition (used by multiple handlers)
  const updateAutoTagConditionMutation = useMutation({
    mutationFn: async ({ conditionId, data }: { conditionId: number; data: any }) => {
      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions/${conditionId}`,
        createRequestOptions('PUT', { auto_tag_condition: data })
      );
      if (!response.ok) throw new Error('Error updating auto tag condition');
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (data && variables.data.updateIndex !== undefined) {
        const tempAutoTagCondition = deepCopy(autoTagConditions);
        tempAutoTagCondition[variables.data.updateIndex] = transformCondition(data);
        setAutoTagConditions(tempAutoTagCondition);
      }
    },
    onError: (error: Error, variables, context) => {
      if (context?.previousAutoTagConditions) {
        setAutoTagConditions(context.previousAutoTagConditions);
      }
      handleMutationError(error, 'Error updating auto tag condition');
    }
  });

  // Handler functions that use the mutations
  const handleAddNewAutoTagCondition = () => addAutoTagConditionMutation.mutate();
  const handleDeleteNewAutoTagCondition = (index: number) => {
    deleteAutoTagConditionMutation.mutate({ index, id: autoTagConditions[index].id });
  };

  const handleNewAutoTagConditionField = (index: number) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const filteredQuestion = radioQuestions?.filter(
      (question) =>
        !tempAutoTagCondition[index].fields.map((f) => f.question.id).includes(question.id)
    );

    tempAutoTagCondition[index].fields.push({
      question: { id: filteredQuestion[0].id, title: filteredQuestion[0].title },
      choice: {
        id: filteredQuestion[0].field_choices[0].id,
        name: filteredQuestion[0].field_choices[0].name
      }
    });

    const previousAutoTagConditions = deepCopy(autoTagConditions);
    setAutoTagConditions(tempAutoTagCondition);

    const newField =
      tempAutoTagCondition[index].fields[tempAutoTagCondition[index].fields.length - 1];
    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: newField.question.id,
          field_choice_id: newField.choice.id,
          tag_ids: tempAutoTagCondition[index].tags.map((tag) => tag.id),
          updateIndex: index
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleRemoveField = (index: number, fieldIndex: number) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].fields.splice(fieldIndex, 1);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    setAutoTagConditions(tempAutoTagCondition);
    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: tempAutoTagCondition[index].fields[0].question.id,
          field_choice_id: tempAutoTagCondition[index].fields[0].choice.id,
          tag_ids: tempAutoTagCondition[index].tags.map((tag) => tag.id),
          updateIndex: index
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleChangeField = (index: number, fieldIndex: number, value: number, type: 'q' | 'a') => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    if (type === 'q') {
      tempAutoTagCondition[index].fields[fieldIndex].question.id = value;
      tempAutoTagCondition[index].fields[fieldIndex].choice.id = radioQuestions.find(
        (question) => question.id === value
      )?.field_choices[0].id;
    } else {
      tempAutoTagCondition[index].fields[fieldIndex].choice.id = value;
    }
    setAutoTagConditions(tempAutoTagCondition);
    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: tempAutoTagCondition[index].fields[fieldIndex].question.id,
          field_choice_id: tempAutoTagCondition[index].fields[fieldIndex].choice.id,
          tag_ids: tempAutoTagCondition[index].tags.map((tag) => tag.id)
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleChangeTags = (index: number, selectedTags: IOptionType[]) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].tags = selectedTags;
    setAutoTagConditions(tempAutoTagCondition);
    updateAutoTagConditionMutation.mutate(
      {
        conditionId: autoTagConditions[index].id,
        data: {
          field_id: tempAutoTagCondition[index].fields[0].question.id,
          field_choice_id: tempAutoTagCondition[index].fields[0].choice.id,
          tag_ids: selectedTags.map((tag) => tag.id)
        }
      },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  const handleEnableCondition = (index: number, conditionId: number) => {
    const tempAutoTagCondition = deepCopy(autoTagConditions);
    const previousAutoTagConditions = deepCopy(autoTagConditions);
    tempAutoTagCondition[index].enabled = !autoTagConditions[index].enabled;
    setAutoTagConditions(tempAutoTagCondition);
    updateAutoTagConditionMutation.mutate(
      { conditionId, data: { enabled: tempAutoTagCondition[index].enabled } },
      { onError: () => setAutoTagConditions(previousAutoTagConditions) }
    );
  };

  // Check if any mutation is loading
  const isPending = updateAutoTagConditionMutation.isPending;
  const isLoading = {
    new: addAutoTagConditionMutation.isPending,
    delete: deleteAutoTagConditionMutation.isPending ? 0 : -1,
    addField: isPending ? 0 : -1,
    deleteField: isPending ? [0, 0] : [-1, -1],
    updateField: isPending ? [0, 0] : [-1, -1]
  };

  return (
    <Box sx={classes.automationContainer}>
      <h2 data-testid="auto-auto tags-header">Auto Tagging</h2>
      <InfoBox message="Any changes here will be automatically saved and will be applied immediately." />
      <Box sx={classes.toggleButtonsContainer}>
        <ToggleButtonGroup value={'questionBased'} exclusive aria-label="question or rating based">
          <ToggleButton
            data-testid="question-based-toggle-button"
            value="questionBased"
            sx={sharedClasses.toggleButton}
          >
            Question Based
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={classes.autoStatusesContainer}>
        <Box sx={{ position: 'relative' }}>
          {radioQuestionsIsLoading ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              height={450}
              width={'100%'}
              sx={{ borderRadius: '8px' }}
            />
          ) : radioQuestions && radioQuestions.length > 0 ? (
            <>
              {autoTagConditions &&
                autoTagConditions?.map((autoTagCondition, statusIndex) => (
                  <Box sx={{ position: 'relative', display: 'flex' }} key={autoTagCondition.id}>
                    <Box sx={classes.autoStatusesSubContainer} key={autoTagCondition.id}>
                      {autoTagCondition.fields?.map((field, fieldIndex) => (
                        <Box
                          sx={classes.statusFormLine}
                          key={field.id}
                          onMouseEnter={() => setHoveredField([statusIndex, fieldIndex])}
                          onMouseLeave={() => setHoveredField([-1, -1])}
                        >
                          <Box
                            component={'h4'}
                            sx={{ marginRight: fieldIndex === 0 ? '31px' : '8px' }}
                          >
                            {fieldIndex === 0 ? 'If' : 'and'}
                          </Box>
                          <FormSelectField
                            label="Variable"
                            isDisabled={!radioQuestions}
                            defaultValue={field.question.id ? field.question.id.toString() : ''}
                            options={radioQuestions
                              .filter(
                                (question) =>
                                  question.id === field.question.id ||
                                  !autoTagCondition.fields
                                    ?.map((f) => f.question.id)
                                    .includes(question.id)
                              )
                              ?.map((question) => ({
                                value: question.id.toString(),
                                label: question.title
                              }))}
                            onChange={(value) =>
                              handleChangeField(statusIndex, fieldIndex, Number(value), 'q')
                            }
                            styles={{ width: '290px' }}
                          />
                          <h4>equals</h4>
                          <FormSelectField
                            label="Answer"
                            options={radioQuestions
                              .filter(
                                (question) =>
                                  question.id === field.question.id || field.question.id === 0
                              )[0]
                              ?.field_choices?.map((choice) => ({
                                value: choice.id.toString(),
                                label: choice.name
                              }))}
                            defaultValue={field.choice.id ? field.choice.id.toString() : ''}
                            onChange={(value) =>
                              handleChangeField(statusIndex, fieldIndex, Number(value), 'a')
                            }
                            styles={{ width: '290px' }}
                            isDisabled={
                              !autoTagConditions[statusIndex]?.fields[fieldIndex]?.question.id ||
                              updateAutoTagConditionMutation.isPending
                            }
                          />
                          {hoveredField[1] > 0 &&
                            hoveredField.join('') === `${statusIndex}${fieldIndex}` && (
                              <IconButton
                                onClick={() => handleRemoveField(statusIndex, fieldIndex)}
                                sx={classes.clearButton}
                              >
                                {updateAutoTagConditionMutation.isPending ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <ClearIcon />
                                )}
                              </IconButton>
                            )}
                        </Box>
                      ))}
                      {autoTagCondition?.fields?.length < radioQuestions?.length && (
                        <Box sx={classes.statusFormLine}>
                          <Button
                            startIcon={
                              updateAutoTagConditionMutation.isPending ? (
                                <CircularProgress size={22} />
                              ) : (
                                <AddIcon />
                              )
                            }
                            onClick={() => handleNewAutoTagConditionField(statusIndex)}
                            sx={classes.addNewFieldButton}
                          >
                            and
                          </Button>
                        </Box>
                      )}
                      <Box sx={classes.statusFormLine}>
                        <h4>then</h4>
                        <Box sx={{ width: '290px' }}>
                          <MultiSelect
                            name="tags"
                            selectOptions={
                              tags
                                ? tags.map((tag) => ({ name: tag.label || tag.value, id: tag.id }))
                                : []
                            }
                            handleFilterChange={(_, newValues) =>
                              handleChangeTags(statusIndex, newValues as IOptionType[])
                            }
                            filters={autoTagCondition.tags || []}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        ...classes.switchContainer,
                        fontWeight: autoTagCondition.enabled ? 'bold' : ''
                      }}
                    >
                      <Box sx={{ marginTop: '12px' }}>
                        <Switch
                          sx={autoTagCondition.enabled ? classes.switchActive : classes.switch}
                          checked={autoTagCondition.enabled}
                          size="small"
                          onChange={() => handleEnableCondition(statusIndex, autoTagCondition.id)}
                        />
                        <Stack sx={{ marginTop: 1, fontSize: '12px' }}>Enabled</Stack>
                      </Box>
                    </Box>
                    <IconButton
                      data-testid="delete-auto-status"
                      sx={classes.deleteButton}
                      onClick={() => handleDeleteNewAutoTagCondition(statusIndex)}
                      disabled={deleteAutoTagConditionMutation.isPending}
                    >
                      {deleteAutoTagConditionMutation.isPending ? (
                        <CircularProgress size={26} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Box>
                ))}
              {autoTagConditions && autoTagConditions.length > 0 ? (
                <IconButton
                  sx={classes.addNewStatusButton}
                  onClick={handleAddNewAutoTagCondition}
                  disabled={addAutoTagConditionMutation.isPending}
                >
                  {addAutoTagConditionMutation.isPending ? (
                    <CircularProgress size={21} />
                  ) : (
                    <AddCircleIcon />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ ...classes.noQuestionsContainer, margin: '100px 0px' }}>
                  <Box>
                    <NoAutoStatusSVG />
                  </Box>
                  <Box sx={classes.noQuestionsText}>You have no auto tagging conditions.</Box>
                  <Button
                    data-testid="add-new-status-button"
                    startIcon={
                      addAutoTagConditionMutation.isPending ? (
                        <CircularProgress size={26} />
                      ) : (
                        <AddCircleIcon />
                      )
                    }
                    onClick={handleAddNewAutoTagCondition}
                    sx={classes.noStatusesAddButton}
                  >
                    Add new Tagging Condition
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ ...classes.noQuestionsContainer, margin: '100px 0px' }}>
              <Box>
                <NoAutoStatusSVG />
              </Box>
              <Box sx={classes.noQuestionsText}>
                Question based auto tags can only be used with radio button question types.
                <br />
                You have currently no radio button questions.
              </Box>
              <Button
                data-testid="create-radio-button"
                startIcon={
                  addAutoTagConditionMutation.isPending ? (
                    <CircularProgress size={26} />
                  ) : (
                    <AddCircleIcon />
                  )
                }
                onClick={(e) => handleEditTabChange && handleEditTabChange(e, 4)}
                sx={classes.noStatusesAddButton}
              >
                Create radio button questions
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
